import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  ClockCounterClockwise, ArrowClockwise, ChartBar,
  Table as TableIcon, X, Warning, ArrowsLeftRight, CaretLeft,
  CheckSquare, Square, FileText, GitDiff,
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDateFR(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
}

function formatDateTimeFR(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso.slice(0, 16);
  }
}

function DriftBadge({ drift }) {
  const moved = drift?.tasks_moved || 0;
  const added = drift?.tasks_added || 0;
  const removed = drift?.tasks_removed || 0;
  const total = moved + added + removed;
  if (total === 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-[#E8F5E9] text-[#2D6A4F] border border-[#2D6A4F33]">
        <ChartBar size={12} weight="bold" /> Aucune dérive
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-[#FFF3E0] text-[#C4773B] border border-[#C4773B66]"
      data-testid="baseline-drift-badge"
      title={`${moved} déplacée(s) · ${added} ajoutée(s) · ${removed} supprimée(s) · max ${drift?.max_drift_days || 0}j`}
    >
      <Warning size={12} weight="fill" /> {moved} dérive{moved > 1 ? 's' : ''}
      {drift?.max_drift_days > 0 && <span className="text-[10px] opacity-75">· {drift.max_drift_days}j max</span>}
    </span>
  );
}

function BaselineDetailPanel({ chantierId, idx, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_URL}/api/chantiers/${chantierId}/planning/baselines/${idx}`, { withCredentials: true })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) toast.error('Erreur chargement détail baseline'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [chantierId, idx]);

  if (loading) {
    return (
      <div className="space-y-2 p-4" data-testid="baseline-detail-loading">
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (!data) return null;

  const handleExport = (format) => {
    const url = `${API_URL}/api/chantiers/${chantierId}/planning/baselines/${idx}/export-pdf?format=${format}`;
    window.open(url, '_blank');
  };

  return (
    <div className="flex flex-col h-full" data-testid="baseline-detail-panel">
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[#E0E0DB] bg-[#FAFAF8]">
        <button
          className="flex items-center gap-1 text-sm text-[#1E3A5F] hover:underline"
          onClick={onBack}
          data-testid="baseline-detail-back"
        >
          <CaretLeft size={16} /> Retour à la liste
        </button>
        <div className="flex gap-2">
          <Button
            variant="outline" size="sm" className="rounded-none gap-1"
            onClick={() => handleExport('table')}
            data-testid="baseline-export-table"
          >
            <TableIcon size={14} /> PDF Tableau
          </Button>
          <Button
            variant="outline" size="sm" className="rounded-none gap-1"
            onClick={() => handleExport('gantt')}
            data-testid="baseline-export-gantt"
          >
            <ChartBar size={14} /> PDF Gantt
          </Button>
        </div>
      </div>

      <div className="px-4 py-3 border-b border-[#E0E0DB]">
        <h3 className="text-base font-bold text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
          {data.label || `Baseline #${idx + 1}`}
        </h3>
        <p className="text-xs text-[#6B6B6B] mt-1">
          Sauvegardée le {formatDateTimeFR(data.date)} · {data.tasks_count} tâche(s)
        </p>
        <div className="mt-2 flex flex-wrap gap-2 text-xs text-[#2C2C2C]">
          <span className="px-2 py-1 bg-[#F5F5F3] border border-[#E0E0DB]">
            <strong>{data.drift?.tasks_moved || 0}</strong> déplacées
          </span>
          <span className="px-2 py-1 bg-[#F5F5F3] border border-[#E0E0DB]">
            <strong>{data.drift?.tasks_added || 0}</strong> ajoutées depuis
          </span>
          <span className="px-2 py-1 bg-[#F5F5F3] border border-[#E0E0DB]">
            <strong>{data.drift?.tasks_removed || 0}</strong> supprimées
          </span>
          <span className="px-2 py-1 bg-[#F5F5F3] border border-[#E0E0DB]">
            Décalage max : <strong>{data.drift?.max_drift_days || 0}j</strong>
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs" data-testid="baseline-detail-table">
          <thead className="sticky top-0 bg-[#1E3A5F] text-white">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">Tâche</th>
              <th className="px-2 py-1.5 text-left font-medium">Entreprise</th>
              <th className="px-2 py-1.5 text-left font-medium">Baseline</th>
              <th className="px-2 py-1.5 text-left font-medium">Actuel</th>
              <th className="px-2 py-1.5 text-right font-medium">Dérive</th>
            </tr>
          </thead>
          <tbody>
            {data.tasks.map((t) => {
              const isAdded = t.added;
              const isRemoved = t.removed;
              const drifted = !isAdded && !isRemoved && (t.drift_days !== 0 || t.duree_changed);
              return (
                <tr
                  key={t.id}
                  className={`border-b border-[#F0F0EE] ${isAdded ? 'bg-[#E8F5E9]' : isRemoved ? 'bg-[#FFEBEE]' : drifted ? 'bg-[#FFF3E0]' : ''}`}
                  data-testid={`baseline-task-row-${t.id}`}
                >
                  <td className="px-2 py-1.5">
                    <div className={`${t.type === 'GROUPE' ? 'font-bold text-[#1E3A5F]' : ''} ${t.type === 'JALON' ? 'italic text-[#D4A84B]' : ''}`}>
                      {t.type === 'GROUPE' ? '► ' : t.type === 'JALON' ? '◆ ' : ''}
                      {t.nom || '(tâche supprimée)'}
                    </div>
                    {t.lot && <div className="text-[10px] text-[#9A9A9A]">{t.lot}</div>}
                  </td>
                  <td className="px-2 py-1.5 text-[#6B6B6B]">{t.entreprise_nom || '—'}</td>
                  <td className="px-2 py-1.5 text-[#2C2C2C]">
                    {t.snapshot ? (
                      <>
                        <div>{formatDateFR(t.snapshot.date_debut)}</div>
                        <div className="text-[10px] text-[#9A9A9A]">{t.snapshot.duree_jours}j → {formatDateFR(t.snapshot.date_fin)}</div>
                      </>
                    ) : (
                      <span className="text-[#2D6A4F] font-medium">— (ajoutée)</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-[#2C2C2C]">
                    {t.current ? (
                      <>
                        <div>{formatDateFR(t.current.date_debut)}</div>
                        <div className="text-[10px] text-[#9A9A9A]">{t.current.duree_jours}j → {formatDateFR(t.current.date_fin)}</div>
                      </>
                    ) : (
                      <span className="text-[#C75B39] font-medium">supprimée</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    {isAdded && <span className="text-[11px] text-[#2D6A4F] font-medium">+ ajoutée</span>}
                    {isRemoved && <span className="text-[11px] text-[#C75B39] font-medium">supprimée</span>}
                    {drifted && (
                      <span className="inline-flex items-center gap-1 text-[11px] text-[#C4773B] font-medium" data-testid={`baseline-task-drift-${t.id}`}>
                        <ArrowsLeftRight size={11} />
                        {t.drift_days > 0 ? `+${t.drift_days}j` : `${t.drift_days}j`}
                        {t.duree_changed && ' · durée'}
                      </span>
                    )}
                    {!isAdded && !isRemoved && !drifted && <span className="text-[11px] text-[#2D6A4F]">aucune</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
// BaselineCompareView — vue côte-à-côte de 2 baselines avec timeline visuelle
// ============================================================

function _toMs(iso) {
  if (!iso) return null;
  const d = new Date(iso.length > 10 ? iso : `${iso}T00:00:00`);
  return isNaN(d.getTime()) ? null : d.getTime();
}

function BaselineCompareView({ data, chantierId, onBack }) {
  if (!data) return null;

  const startMs = _toMs(data.range_start);
  const endMs = _toMs(data.range_end);
  const totalMs = (startMs && endMs && endMs > startMs) ? (endMs - startMs) : 1;

  const pct = (iso) => {
    const ms = _toMs(iso);
    if (ms === null || !startMs) return 0;
    return Math.max(0, Math.min(100, ((ms - startMs) / totalMs) * 100));
  };

  const m = data.metrics || {};
  const totalDays = totalMs / 86400000;
  // 5 graduations
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((p) => {
    if (!startMs) return null;
    const d = new Date(startMs + p * totalMs);
    return { p: p * 100, label: d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) };
  }).filter(Boolean);

  const exportPdf = () => {
    const url = `${API_URL}/api/chantiers/${chantierId}/planning/baselines-compare/export-pdf?a=${data.a.index}&b=${data.b.index}`;
    window.open(url, '_blank');
  };

  return (
    <div className="flex flex-col flex-1 overflow-hidden" data-testid="baseline-compare-view">
      {/* Header avec labels A et B */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-[#E0E0DB] bg-[#FAFAF8]">
        <button
          className="flex items-center gap-1 text-sm text-[#1E3A5F] hover:underline"
          onClick={onBack}
          data-testid="baseline-compare-back"
        >
          <CaretLeft size={16} /> Retour à la liste
        </button>
        <Button
          variant="outline" size="sm" className="rounded-none gap-1"
          onClick={exportPdf}
          data-testid="baseline-compare-export-pdf"
        >
          <FileText size={14} /> Rapport de dérive PDF
        </Button>
      </div>

      <div className="px-4 py-3 border-b border-[#E0E0DB] bg-white">
        <div className="grid grid-cols-2 gap-4 mb-3">
          <div className="border-l-4 border-[#9A9A9A] pl-3">
            <p className="text-[10px] uppercase tracking-wide text-[#9A9A9A] font-bold">Version A</p>
            <h3 className="text-sm font-bold text-[#2C2C2C] truncate" data-testid="baseline-compare-label-a">
              {data.a.label || `Baseline #${data.a.index + 1}`}
            </h3>
            <p className="text-xs text-[#6B6B6B]">{formatDateTimeFR(data.a.date)} · {data.a.tasks_count} tâches</p>
          </div>
          <div className="border-l-4 border-[#C4773B] pl-3">
            <p className="text-[10px] uppercase tracking-wide text-[#C4773B] font-bold">Version B</p>
            <h3 className="text-sm font-bold text-[#2C2C2C] truncate" data-testid="baseline-compare-label-b">
              {data.b.label || `Baseline #${data.b.index + 1}`}
            </h3>
            <p className="text-xs text-[#6B6B6B]">{formatDateTimeFR(data.b.date)} · {data.b.tasks_count} tâches</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 text-xs" data-testid="baseline-compare-metrics">
          <span className="px-2 py-1 bg-[#F5F5F3] border border-[#E0E0DB]">
            <strong>{m.tasks_total || 0}</strong> tâches
          </span>
          <span className={`px-2 py-1 border ${m.tasks_moved ? 'bg-[#FFF3E0] border-[#C4773B66] text-[#C4773B]' : 'bg-[#F5F5F3] border-[#E0E0DB]'}`}>
            <strong>{m.tasks_moved || 0}</strong> déplacée(s)
          </span>
          <span className={`px-2 py-1 border ${m.tasks_duree_changed ? 'bg-[#FFF3E0] border-[#C4773B66] text-[#C4773B]' : 'bg-[#F5F5F3] border-[#E0E0DB]'}`}>
            <strong>{m.tasks_duree_changed || 0}</strong> durée modifiée
          </span>
          <span className={`px-2 py-1 border ${m.tasks_only_in_b ? 'bg-[#E8F5E9] border-[#2D6A4F66] text-[#2D6A4F]' : 'bg-[#F5F5F3] border-[#E0E0DB]'}`}>
            <strong>{m.tasks_only_in_b || 0}</strong> ajoutée(s) en B
          </span>
          <span className={`px-2 py-1 border ${m.tasks_only_in_a ? 'bg-[#FFEBEE] border-[#C75B3966] text-[#C75B39]' : 'bg-[#F5F5F3] border-[#E0E0DB]'}`}>
            <strong>{m.tasks_only_in_a || 0}</strong> supprimée(s)
          </span>
          <span className="px-2 py-1 bg-[#F5F5F3] border border-[#E0E0DB]">
            Décalage max : <strong>{m.max_drift_days || 0}j</strong>
          </span>
        </div>
      </div>

      {/* Timeline diff */}
      <div className="flex-1 overflow-auto" data-testid="baseline-compare-timeline">
        {!data.tasks?.length ? (
          <div className="p-8 text-center text-sm text-[#9A9A9A]">Aucune tâche à comparer.</div>
        ) : (
          <table className="w-full text-xs" style={{ minWidth: 900 }}>
            <thead className="sticky top-0 bg-white border-b-2 border-[#1E3A5F] z-10">
              <tr>
                <th className="px-2 py-1.5 text-left font-medium text-[#2C2C2C]" style={{ width: '30%' }}>Tâche</th>
                <th className="px-2 py-1.5 text-left font-medium text-[#2C2C2C]" style={{ width: '50%' }}>
                  Timeline
                  <div className="relative h-3 mt-1 border-b border-[#E0E0DB]">
                    {ticks.map((t, i) => (
                      <span
                        key={i}
                        className="absolute top-0 text-[9px] text-[#9A9A9A]"
                        style={{ left: `${t.p}%`, transform: i === ticks.length - 1 ? 'translateX(-100%)' : 'none' }}
                      >
                        {t.label}
                      </span>
                    ))}
                  </div>
                </th>
                <th className="px-2 py-1.5 text-right font-medium text-[#2C2C2C]" style={{ width: '20%' }}>Dérive</th>
              </tr>
            </thead>
            <tbody>
              {data.tasks.map((t) => {
                const driftLabel = t.only_in_a
                  ? 'supprimée en B'
                  : t.only_in_b
                  ? 'ajoutée en B'
                  : t.drift_days || t.duree_changed
                  ? `${t.drift_days > 0 ? '+' : ''}${t.drift_days}j${t.duree_changed ? ' · durée Δ' : ''}`
                  : 'inchangée';
                const driftClass = t.only_in_a
                  ? 'text-[#C75B39]'
                  : t.only_in_b
                  ? 'text-[#2D6A4F]'
                  : (t.drift_days || t.duree_changed)
                  ? 'text-[#C4773B] font-medium'
                  : 'text-[#9A9A9A]';
                const aVis = t.a;
                const bVis = t.b;
                return (
                  <tr
                    key={t.id}
                    className="border-b border-[#F0F0EE] hover:bg-[#FAFAF8]"
                    data-testid={`baseline-compare-row-${t.id}`}
                  >
                    <td className="px-2 py-2 align-top">
                      <div className={`${t.type === 'GROUPE' ? 'font-bold text-[#1E3A5F]' : ''} ${t.type === 'JALON' ? 'italic text-[#D4A84B]' : ''}`}>
                        {t.type === 'GROUPE' ? '► ' : t.type === 'JALON' ? '◆ ' : ''}
                        {t.nom}
                      </div>
                      {t.lot && <div className="text-[10px] text-[#9A9A9A]">{t.lot}{t.entreprise_nom ? ` · ${t.entreprise_nom}` : ''}</div>}
                    </td>
                    <td className="px-2 py-2 align-middle">
                      <div className="space-y-1">
                        {/* Bar A */}
                        <div className="relative h-4 bg-[#F5F5F3]" title={aVis ? `A: ${formatDateFR(aVis.date_debut)} → ${formatDateFR(aVis.date_fin)} (${aVis.duree_jours}j)` : 'Absente de A'}>
                          {aVis && aVis.date_debut && (
                            <div
                              className="absolute top-0 h-full bg-[#9A9A9A]"
                              style={{
                                left: `${pct(aVis.date_debut)}%`,
                                width: `${Math.max(0.5, pct(aVis.date_fin) - pct(aVis.date_debut))}%`,
                              }}
                              data-testid={`compare-bar-a-${t.id}`}
                            >
                              <span className="absolute inset-0 px-1 text-[9px] text-white truncate flex items-center">
                                {aVis.duree_jours}j
                              </span>
                            </div>
                          )}
                          {!aVis && <span className="absolute inset-0 px-1 text-[9px] text-[#C75B39] italic flex items-center">absente</span>}
                        </div>
                        {/* Bar B */}
                        <div className="relative h-4 bg-[#F5F5F3]" title={bVis ? `B: ${formatDateFR(bVis.date_debut)} → ${formatDateFR(bVis.date_fin)} (${bVis.duree_jours}j)` : 'Absente de B'}>
                          {bVis && bVis.date_debut && (
                            <div
                              className={`absolute top-0 h-full ${t.drift_days || t.duree_changed ? 'bg-[#C4773B]' : 'bg-[#1E3A5F]'}`}
                              style={{
                                left: `${pct(bVis.date_debut)}%`,
                                width: `${Math.max(0.5, pct(bVis.date_fin) - pct(bVis.date_debut))}%`,
                              }}
                              data-testid={`compare-bar-b-${t.id}`}
                            >
                              <span className="absolute inset-0 px-1 text-[9px] text-white truncate flex items-center">
                                {bVis.duree_jours}j
                              </span>
                            </div>
                          )}
                          {!bVis && <span className="absolute inset-0 px-1 text-[9px] text-[#C75B39] italic flex items-center">absente</span>}
                        </div>
                      </div>
                    </td>
                    <td className={`px-2 py-2 text-right text-[11px] ${driftClass} align-middle`}>
                      {driftLabel}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="border-t border-[#E0E0DB] bg-[#FAFAF8] px-4 py-2 text-[11px] text-[#6B6B6B] flex items-center gap-3">
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-2.5 bg-[#9A9A9A]" /> Version A</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-2.5 bg-[#1E3A5F]" /> Version B (identique)</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-2.5 bg-[#C4773B]" /> Version B (modifiée)</span>
      </div>
    </div>
  );
}

export default function PlanningVersionsModal({ open, onClose, chantierId }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIdx, setSelectedIdx] = useState(null);
  // Mode comparaison de 2 baselines
  const [compareMode, setCompareMode] = useState(false);
  const [compareSel, setCompareSel] = useState([]); // [a, b]
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);

  const loadList = () => {
    setLoading(true);
    axios.get(`${API_URL}/api/chantiers/${chantierId}/planning/baselines`, { withCredentials: true })
      .then((r) => setItems(r.data?.baselines || []))
      .catch(() => toast.error('Erreur chargement baselines'))
      .finally(() => setLoading(false));
  };

  const launchCompare = (a, b) => {
    setCompareLoading(true);
    setCompareData(null);
    axios.get(`${API_URL}/api/chantiers/${chantierId}/planning/baselines-compare?a=${a}&b=${b}`, { withCredentials: true })
      .then((r) => setCompareData(r.data))
      .catch(() => toast.error('Erreur chargement comparaison'))
      .finally(() => setCompareLoading(false));
  };

  const toggleCompareSel = (idx) => {
    setCompareSel((prev) => {
      if (prev.includes(idx)) return prev.filter((x) => x !== idx);
      if (prev.length >= 2) return [prev[1], idx]; // remplace le plus ancien
      return [...prev, idx];
    });
  };

  const exitCompareMode = () => {
    setCompareMode(false);
    setCompareSel([]);
    setCompareData(null);
  };

  useEffect(() => {
    if (!open) {
      setItems(null);
      setSelectedIdx(null);
      exitCompareMode();
      return;
    }
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, chantierId]);

  // Vue comparaison résultat (compareData chargée)
  if (compareData) {
    return (
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
        <DialogContent className="max-w-6xl p-0 max-h-[90vh] flex flex-col" data-testid="planning-versions-modal">
          <DialogHeader className="px-4 py-3 border-b border-[#E0E0DB] bg-[#FAFAF8]">
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
              <GitDiff size={20} className="text-[#1E3A5F]" />
              Comparaison de baselines
            </DialogTitle>
          </DialogHeader>
          <BaselineCompareView
            data={compareData}
            chantierId={chantierId}
            onBack={() => { setCompareData(null); }}
          />
          <div className="border-t border-[#E0E0DB] bg-[#FAFAF8] px-4 py-2 flex items-center justify-end">
            <button
              className="text-xs text-[#6B6B6B] hover:text-[#1E3A5F] flex items-center gap-1"
              onClick={onClose}
              data-testid="planning-versions-close"
            >
              <X size={12} /> Fermer
            </button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-4xl p-0 max-h-[90vh] flex flex-col" data-testid="planning-versions-modal">
        <DialogHeader className="px-4 py-3 border-b border-[#E0E0DB] bg-[#FAFAF8]">
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
            <ClockCounterClockwise size={20} className="text-[#1E3A5F]" />
            Historique du planning
          </DialogTitle>
        </DialogHeader>

        {selectedIdx !== null ? (
          <BaselineDetailPanel
            chantierId={chantierId}
            idx={selectedIdx}
            onBack={() => setSelectedIdx(null)}
          />
        ) : (
          <div className="flex-1 overflow-auto" data-testid="planning-versions-list">
            {/* Toolbar mode comparaison */}
            {!loading && items?.length >= 2 && (
              <div className="px-4 py-2 border-b border-[#E0E0DB] bg-[#FAFAF8] flex items-center justify-between gap-3">
                {!compareMode ? (
                  <button
                    className="text-xs text-[#1E3A5F] hover:underline flex items-center gap-1 font-medium"
                    onClick={() => setCompareMode(true)}
                    data-testid="planning-versions-compare-mode"
                  >
                    <GitDiff size={14} /> Comparer 2 baselines (avant/après)
                  </button>
                ) : (
                  <>
                    <p className="text-xs text-[#2C2C2C]">
                      <strong>{compareSel.length}/2</strong> baselines sélectionnées
                      {compareSel.length === 2 && <span className="text-[#9A9A9A]"> · prêt à comparer</span>}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline" size="sm" className="rounded-none gap-1"
                        onClick={exitCompareMode}
                        data-testid="planning-versions-compare-cancel"
                      >
                        <X size={12} /> Annuler
                      </Button>
                      <Button
                        size="sm"
                        className="bg-[#1E3A5F] hover:bg-[#15293F] text-white rounded-none gap-1 disabled:opacity-50"
                        disabled={compareSel.length !== 2}
                        onClick={() => launchCompare(compareSel[0], compareSel[1])}
                        data-testid="planning-versions-compare-launch"
                      >
                        <GitDiff size={12} /> Comparer
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            {loading && (
              <div className="p-4 space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            )}
            {compareLoading && (
              <div className="p-4 text-center text-sm text-[#9A9A9A]" data-testid="planning-versions-compare-loading">
                Chargement de la comparaison…
              </div>
            )}
            {!loading && items?.length === 0 && (
              <div className="p-8 text-center text-sm text-[#9A9A9A]" data-testid="planning-versions-empty">
                Aucune baseline enregistrée pour ce planning.
                <br />
                <span className="text-xs">Cliquez sur « Baseline » dans la barre d'outils pour archiver l'état actuel.</span>
              </div>
            )}
            {!loading && items?.length > 0 && (
              <ul className="divide-y divide-[#E0E0DB]">
                {[...items].reverse().map((bl) => {
                  const isSelected = compareSel.includes(bl.index);
                  return (
                    <li
                      key={bl.index}
                      className={`p-4 hover:bg-[#FAFAF8] transition-colors flex items-center gap-4 ${isSelected ? 'bg-[#EAF1FA] border-l-4 border-[#1E3A5F]' : ''}`}
                      data-testid={`planning-version-row-${bl.index}`}
                    >
                      {compareMode && (
                        <button
                          onClick={() => toggleCompareSel(bl.index)}
                          className="flex-shrink-0"
                          data-testid={`planning-version-select-${bl.index}`}
                          aria-label={isSelected ? 'Désélectionner' : 'Sélectionner pour comparaison'}
                        >
                          {isSelected ? (
                            <CheckSquare size={20} weight="fill" className="text-[#1E3A5F]" />
                          ) : (
                            <Square size={20} className="text-[#9A9A9A]" />
                          )}
                        </button>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2">
                          <h4 className="text-sm font-bold text-[#2C2C2C] truncate">
                            {bl.label || `Baseline #${bl.index + 1}`}
                          </h4>
                          <DriftBadge drift={bl.drift} />
                        </div>
                        <p className="text-xs text-[#6B6B6B] mt-0.5">
                          {formatDateTimeFR(bl.date)} · {bl.tasks_count} tâche(s)
                        </p>
                      </div>
                      {!compareMode && (
                        <div className="flex gap-2">
                          <Button
                            variant="outline" size="sm" className="rounded-none gap-1"
                            onClick={() => setSelectedIdx(bl.index)}
                            data-testid={`planning-version-view-${bl.index}`}
                          >
                            <ArrowsLeftRight size={14} /> Comparer
                          </Button>
                          <Button
                            variant="outline" size="sm" className="rounded-none gap-1"
                            onClick={() => window.open(`${API_URL}/api/chantiers/${chantierId}/planning/baselines/${bl.index}/export-pdf?format=table`, '_blank')}
                            data-testid={`planning-version-export-table-${bl.index}`}
                          >
                            <TableIcon size={14} /> PDF
                          </Button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        <div className="border-t border-[#E0E0DB] bg-[#FAFAF8] px-4 py-2 flex items-center justify-between">
          <button
            className="text-xs text-[#6B6B6B] hover:text-[#1E3A5F] flex items-center gap-1"
            onClick={loadList}
            data-testid="planning-versions-refresh"
          >
            <ArrowClockwise size={12} /> Actualiser
          </button>
          <button
            className="text-xs text-[#6B6B6B] hover:text-[#1E3A5F] flex items-center gap-1"
            onClick={onClose}
            data-testid="planning-versions-close"
          >
            <X size={12} /> Fermer
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
